import { PaymentGatewayService, LocalService } from "../../core/services";
// import { RazorpayOptions } from 'react-razorpay';
import { CreateOrder, CreateSubscription } from "custom-type";
import { PAYMENT_GATEWAY_KEY } from "../../config/config";
import Logo from "../../assets/images/clan_connect_logo.png";
import * as gtUtil from "../../core/utility";
import { askBillingState, type BillingStateOption } from "../../components/billing-state/BillingStatePrompt";

const paymentGatewayService: PaymentGatewayService = new PaymentGatewayService();

const localService: LocalService = new LocalService();

// Build the Razorpay `contact` prefill. For FOREIGN plans (non-INR) the number is
// prefixed with the user's dialing code so Razorpay uses E.164 (e.g. +12542312713)
// instead of assuming +91 and rejecting a valid foreign number ("Please enter your
// mobile number"). The Indian (INR) flow is left EXACTLY as-is: the raw 10-digit
// number under Razorpay's default +91.
function buildRazorpayContact(props: any, data: any): string {
  const raw = String(props?.UserReducer?.login_mobile ?? '');
  const isForeign = !!data?.currency && String(data.currency).toUpperCase() !== 'INR';
  if (!isForeign) return raw;
  const dc = String(props?.UserReducer?.dialing_code ?? '').replace(/[^0-9]/g, '');
  const num = raw.replace(/[^0-9]/g, '');
  return dc && num ? `+${dc}${num}` : raw;
}

async function createOrder(orderDetail: CreateOrder) {
  try {
    const response: any = await paymentGatewayService.createOrder(orderDetail);
    return response;
  } catch (error) {
    throw new Error(`CreaterOrder`);
  }
}

async function createAutoSubscription(orderDetail: CreateSubscription) {
  try {
    // window.cctag('event', 'add_to_cart', {
    //   currency: 'INR',
    //   value: orderDetail.amount,
    //   items: [{
    //     item_id: orderDetail.razorpay_plan_id,
    //     item_name: `Influencer Subscription - ${orderDetail.plan_id}`,
    //     price: orderDetail.amount,
    //     quantity: 1
    //   }]
    // });
    const response: any = await paymentGatewayService.createAutoSubscription(orderDetail);
    return response;
  } catch (error) {
    throw new Error(`CreateAutoSubscription Error: ${error}`);
  }
}

async function updateOrderDetail(payment_id: string, props: any) {
  try {
    const response = await paymentGatewayService.updateOrderDetail({
      user_id: props.UserReducer.user_id,
      account_id: props.UserReducer.account_id,
      influencer_id: props.UserReducer.influencer_id,
      account_type: props.UserReducer.account_type,
      account_name: props.UserReducer.account_name,
      login_email: props.UserReducer.login_email,
      login_mobile: props.UserReducer.login_mobile,
      razorpay_payment_id: payment_id,
      campaign_id: props.campaignReducer?.createCampaign?.campaign_id || props?.selectedCampaign?.campaign_id || 0,
      brand_id: props.UserReducer?.brand_id
    });
    if (response.status === gtUtil.Constants.SUCCESS) {
      return payment_id
    } else {
      localService.toastify(response.info, "error", 1000);
      return ''
    }
  } catch (error) {
    console.error(`Update Order Detail Error :: ${error}`);
    return ''
  } finally {
    // getSubscriptionDetail();
  }
}

async function getSubscriptionPaymentInfo(mandateResponse: {razorpay_payment_id: string, razorpay_signature: string, razorpay_subscription_id: string}, props: any) {
  try {
    const response = await paymentGatewayService.getSubscriptionPaymentInfo({
      user_id: props.UserReducer.user_id,
      account_id: props.UserReducer.account_id,
      influencer_id: props.UserReducer.influencer_id,
      account_type: props.UserReducer.account_type,
      account_name: props.UserReducer.account_name,
      login_email: props.UserReducer.login_email,
      login_mobile: props.UserReducer.login_mobile,
      razorpay_payment_id: mandateResponse.razorpay_payment_id,
      razorpay_signature: mandateResponse.razorpay_signature,
      razorpay_subscription_id: mandateResponse.razorpay_subscription_id,
      campaign_id: props.campaignReducer?.createCampaign?.campaign_id || props?.selectedCampaign?.campaign_id || 0,
      brand_id: props.UserReducer?.brand_id
    });
    // console.log('mandateResponse', mandateResponse);
    // console.log('response', response);
    // if (response.status === gtUtil.Constants.SUCCESS) {
    //   return mandateResponse.razorpay_payment_id
    // } else {
    //   localService.toastify(response.info, "error", 1000);
    //   return ''
    // }
    return {razorpay_payment_id: mandateResponse.razorpay_payment_id, razorpay_subscription_id: mandateResponse.razorpay_subscription_id}
  } catch (error) {
    console.error(`Update Order Detail Error :: ${error}`);
    return {}
  } finally {
    // getSubscriptionDetail();
  }
}

const loadRazorpayScript = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Before an Indian (INR) purchase: the paying account's own location must have
 * its state — the GST invoice's state (API api/billing/billing-state.ts). When
 * it has none, asks for it ("Select your state"), saves it and lets the payment
 * continue. Every role (creator, brand, agency, Talent Partner) and the app's web
 * checkout (props.checkoutKey: its `?key=`, there is no login token there).
 * Returns false only when the dialog was closed or the state couldn't be saved —
 * nothing is charged then. If the check itself fails, the payment goes ahead as
 * before (the invoice then has no state rather than a wrong one).
 */
async function ensureBillingState(data: any, props: any): Promise<boolean> {
  if (String(data?.currency || 'INR').toUpperCase() !== 'INR') return true;
  const account_id = Number(props?.UserReducer?.account_id) || 0;
  if (!account_id) return true;
  const key = props?.checkoutKey ? String(props.checkoutKey) : undefined;
  let states: BillingStateOption[] = [];
  try {
    const status: any = await paymentGatewayService.billingStateStatus({ account_id, key });
    if (status?.status !== gtUtil.Constants.SUCCESS || !status?.data?.required) return true;
    states = Array.isArray(status.data.states) ? status.data.states : [];
  } catch (error) {
    console.error('Billing state check error:', error);
    return true;
  }
  if (!states.length) return true;
  const stateId = await askBillingState(states);
  if (!stateId) return false;
  try {
    const saved: any = await paymentGatewayService.saveBillingState({ account_id, state_id: stateId, key });
    if (saved?.status === gtUtil.Constants.SUCCESS) return true;
    localService.toastify(saved?.info || 'Could not save your state. Please try again.', 'error', 3000);
  } catch (error) {
    console.error('Billing state save error:', error);
    localService.toastify('Could not save your state. Please try again.', 'error', 3000);
  }
  return false;
}

// async function makeRazorpayPayment(data: any, props: any, razorpay: any): Promise<any> {
async function makeRazorpayPayment(data: any, props: any, coupon_code: string = ''): Promise<any> {
  const res = {
    status: false,
    paymentId: '',
    message: 'Payment failed.',
    error: null
  }
  try {
    if (!(await ensureBillingState(data, props))) {
      res.message = 'Payment cancelled';
      return res;
    }
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      res.message = 'Razorpay SDK failed to load';
      return res;
    }
    // First Create order
    const razorpayOrder: any = await createOrder({
      user_id: props.UserReducer.user_id,
      account_id: props.UserReducer.account_id,
      influencer_id: props.UserReducer.influencer_id,
      account_type: props.UserReducer.account_type,
      account_name: props.UserReducer.account_name,
      login_email: props.UserReducer.login_email,
      login_mobile: props.UserReducer.login_mobile,
      plan_id: data.plan_id,
      // When a special coupon is applied, the backend re-validates it and
      // recomputes the chargeable amount server-side (this FE amount is ignored).
      coupon_code: coupon_code || undefined,
      orderDetail: {
        amount: data.amount + '00',
        currency: data.currency || 'INR',
        notes: {
          key1: coupon_code ? 'LS coupon purchase' : 'LS purchase',
        },
      },
      campaign_id: props.campaignReducer?.createCampaign?.campaign_id || props?.selectedCampaign?.campaign_id || 0,
      brand_id: props.UserReducer?.brand_id
    });

    // If Order is Created, the User Makes a Payment
    if (razorpayOrder.status === gtUtil.Constants.SUCCESS) {
      const paymentPromise = new Promise<any>((resolve, reject) => {
        // const RAZORPAY_OPTIONS: (RazorpayOptions) = {
        const RAZORPAY_OPTIONS = {
          key: PAYMENT_GATEWAY_KEY,
          amount: razorpayOrder.data.rows[0].amount_due + '00',
          currency: data.currency || 'INR',
          name: 'ClanConnect',
          description: `${data?.user_type} ${data.description}`,
          image: Logo,
          order_id: razorpayOrder.data.rows[0].id,
          handler: function (response: any) {
            // Payment successful
            updateOrderDetail(response.razorpay_payment_id, props)
              .then((response) => {
                res.status = true;
                res.message = 'Payment success'
                res.paymentId = response
                resolve(res);
              })
              .catch(error => {
                res.status = false;
                res.message = 'Payment failed'
                res.error = error
                console.error("Update Order Detail Error:", error);
                reject(res); // Reject with appropriate error message
              });
          },
          prefill: {
            name: props.UserReducer.account_name,
            email: props.UserReducer.login_email,
            contact: buildRazorpayContact(props, data),
          },
          notes: {
            address: 'Razorpay Corporate Office',
          },
          theme: {
            color: '#00aae5',
          },
          modal: {
            ondismiss: function () {
              res.status = false;
              res.message = 'Payment cancelled'
              reject(res); // Reject with appropriate error message on cancellation
            }
          }
        };
        // const rzpay = new razorpay(RAZORPAY_OPTIONS);
        // rzpay.open();
        const rzp = new window.Razorpay(RAZORPAY_OPTIONS);
        rzp.open();
        // e.preventDefault();
      });

      return paymentPromise;
    } else {
      // Order creation failed
      localService.toastify(razorpayOrder.info);
      return res
    }
  } catch (error: any) {
    // Error occurred during payment process
    res.error = error;
    console.error("Make Payment Error:", error);
    return res;
  }
}

async function makeRazorpayAutoPay(data: any, props: any): Promise<any> {
  const res = {
    status: false,
    paymentId: '',
    razorpaySubscriptionId: '',
    message: 'Payment failed.',
    error: null
  }
  try {
    if (!(await ensureBillingState(data, props))) {
      res.message = 'Payment cancelled';
      return res;
    }
    const isLoaded = await loadRazorpayScript();

    if (!isLoaded) {
      res.message = 'Razorpay SDK failed to load';
      return res;
    }

    // const subRes: any = await createAutoSubscription(data, props);

    // First Create Subscription

    // subscription order detail example
    // {
    //   plan_id: razorpay_plan_id,
    //   customer_notify: 1,
    //   quantity: 1,
    //   total_count: 36, // to be set as per requirement, currently set to a very high value to mimic an 'infinite' subscription
    //   notes: {
    //     user_id,
    //     plan_id,
    //     account_id
    //   }
    // }
    const subRes: any = await createAutoSubscription({
      razorpay_plan_id: data.razorpay_plan_id,
      user_id: props.UserReducer.user_id,
      account_id: props.UserReducer.account_id,
      influencer_id: props.UserReducer.influencer_id,
      account_type: props.UserReducer.account_type,
      account_name: props.UserReducer.account_name,
      login_email: props.UserReducer.login_email,
      login_mobile: props.UserReducer.login_mobile,
      plan_id: data.plan_id,
      amount: data.amount,
      orderDetail: {
        plan_id: data.razorpay_plan_id,
        customer_notify: 1,
        quantity: 1,
        total_count: data.plan_type.toLowerCase() === 'monthly' ? 36 : 5, // to be set as per requirement, currently set to a very high value to mimic an 'infinite' subscription
        // amount: data.amount + '00',
        // currency: 'INR',
        notes: {
          key1: 'LS subscription purchase',
        },
      },
      campaign_id: props.campaignReducer?.createCampaign?.campaign_id || props?.selectedCampaign?.campaign_id || 0,
      brand_id: props.UserReducer?.brand_id
    });

    if (subRes.status !== gtUtil.Constants.SUCCESS) {
      // throw new Error(subRes.info);
      // Subscription creation failed
      localService.toastify(subRes.info);
      return res
    }

    // window.cctag('event', 'initiate_checkout', {
    //   currency: 'INR',
    //   value: data.amount,
    //   items: [{
    //     item_id: data.razorpay_plan_id,
    //     item_name: `Influencer Subscription - ${data.plan_id}`,
    //     price: data.amount,
    //     quantity: 1
    //   }]
    // });

    const subscriptionId = subRes.data.rows[0].id;

    const paymentPromise = new Promise<any>((resolve, reject) => {
      // const RAZORPAY_OPTIONS: (RazorpayOptions) = {
      const RAZORPAY_OPTIONS = {
        key: PAYMENT_GATEWAY_KEY,
        subscription_id: subscriptionId, // ⭐ CRITICAL
        // amount: razorpayOrder.data.rows[0].amount_due + '00',
        currency: data.currency || 'INR',
        name: 'ClanConnect',
        description: data.description,
        image: Logo,
        // order_id: razorpayOrder.data.rows[0].id,
        handler: function (response: any) {
          // console.log('handler response', response);
          // Payment successful
          getSubscriptionPaymentInfo(response, props)
            .then((response) => {
              res.status = true;
              res.message = 'Payment success'
              res.paymentId = response.razorpay_payment_id || ''
              res.razorpaySubscriptionId = response.razorpay_subscription_id || ''
              // window.cctag('event', 'purchase', {
              //   transaction_id: response.razorpay_subscription_id || '',
              //   currency: 'INR',
              //   value: data.amount,
              //   items: [{
              //     item_id: response.razorpay_payment_id || '',
              //     item_name: `Influencer Subscription - ${data.plan_id}`,
              //     price: data.amount,
              //     quantity: 1
              //   }]
              // });
              resolve(res);
            })
            .catch(error => {
              res.status = false;
              res.message = 'Payment failed'
              res.error = error
              console.error("Update Order Detail Error:", error);
              reject(res); // Reject with appropriate error message
            });
        },
        prefill: {
          name: props.UserReducer.account_name,
          email: props.UserReducer.login_email,
          contact: buildRazorpayContact(props, data),
        },
        notes: {
          address: 'Razorpay Corporate Office',
        },
        theme: {
          color: '#00aae5',
        },
        modal: {
          ondismiss: function () {
            res.status = false;
            res.message = 'Payment cancelled'
            reject(res); // Reject with appropriate error message on cancellation
          }
        }
      };

      const rzp = new window.Razorpay(RAZORPAY_OPTIONS);
      rzp.open();
    });

    return paymentPromise;
  } catch (error: any) {
    // Error occurred during payment process
    res.error = error;
    console.error("Make Payment Error:", error);
    return res;
  }
  
}

/**
 * Whether a plan row can be sold as a Razorpay autopay (auto-renewing)
 * subscription: it carries a real Razorpay plan id ("plan_…"), not a
 * placeholder such as "clan_brand_monthly_plan", which Razorpay would refuse.
 */
const hasRazorpayPlan = (plan: any): boolean => /^plan_[A-Za-z0-9]+$/.test(String(plan?.razorpay_plan_id ?? '').trim());

/**
 * A brand (or an agency, for the brand it is working on) buying a subscription
 * plan: always Razorpay autopay, as creators' plans. One-time brand plans are
 * retired, so a plan row without a real Razorpay plan id is not sold at all
 * (never charged as a one-time payment). The one-time purchases — New Barter
 * Campaign, Upgrade Barter Proposal Limit and the legacy campaign ones — don't
 * come here; they keep makeRazorpayPayment.
 */
async function makeBrandSubscriptionPayment(data: any, props: any): Promise<any> {
  if (!hasRazorpayPlan(data)) {
    localService.toastify("This plan can't be bought right now. Please contact support.", 'error', 3000);
    return { status: false, paymentId: '', razorpaySubscriptionId: '', message: 'Plan not available for autopay.', error: null };
  }
  return makeRazorpayAutoPay(data, props);
}

export { makeRazorpayPayment, makeRazorpayAutoPay, makeBrandSubscriptionPayment, hasRazorpayPlan };